// The cells of David's Monday table, as billing-ops report_exec_summary draws them.
//
// One module, two readers: the Ledger review card on Practice Details (one row, this
// practice) and the Exec review page (one row per practice). Each cell composes the
// same figures the PDF page did: the headline rate with its matured window, the
// insurance/patient split in whole percents, the open balance; the trust percentage,
// tag and note; the "why" prose with claims in flight and the top denial under it;
// the "what" prose with its owner line. Counts and rates only, never a patient.
//
// Since 2026-10-13 the headline rate is the net collection rate on allowed amounts
// (row.rate), and the "Why" cell names the largest bucket of the gap to 98%
// (row.largest_gap); the legacy rate against derived expected rides along as
// row.expected_rate_legacy for one release and is printed small under the headline.
//
// Since 2026-10-29 the open balance is at allowed for a practice whose payers allow
// under half of billed (row.ar_basis.basis "allowed"), with row.open_balance_billed
// beside it; the ratio is printed under it as the row's data-quality line.

import React from "react";
import { arBasisLine, isAllowedBasis } from "../../utils/arBasis";

export const DASH = "—";
export const NO_NOTES = "No notes this week";

const MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function monthLabel(key, current = false) {
  const label = MONTH[parseInt(key.slice(5, 7), 10) - 1] || key;
  return current ? `${label} (to date)` : label;
}

// "Oct ’25": a month with its year, for a row of thirteen where a month name alone
// would repeat (the patient collections row).
export function monthYearLabel(key) {
  const label = MONTH[parseInt(key.slice(5, 7), 10) - 1];
  return label ? `${label} ’${key.slice(2, 4)}` : key;
}

// "May–Jul services", one month, or none yet (report_ledger_review.months_label).
export function monthsLabel(keys) {
  if (!keys || keys.length === 0) return "No matured months yet";
  if (keys.length === 1) return `${monthLabel(keys[0])} services`;
  return `${monthLabel(keys[0])}–${monthLabel(keys[keys.length - 1])} services`;
}

// The headline rate: one decimal, as the exec page prints it.
export function pct1(value) {
  if (value === null || value === undefined) return DASH;
  return `${Number(value).toFixed(1)}%`;
}

// The split and the monthly rates: whole percents, as the practice pages print them.
export function pct0(value) {
  if (value === null || value === undefined) return DASH;
  return `${Math.round(Number(value))}%`;
}

// Whole dollars for a skim: "$1,234"; a net that comes out negative (a takeback
// larger than the week's receipts) is "-$120", and one that rounds to nothing is "$0".
export function money(value) {
  if (value === null || value === undefined || value === "") return DASH;
  const n = Number(value);
  const text = Math.abs(n).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return `${n < 0 && text !== "0" ? "-" : ""}$${text}`;
}

// Cents where the figure is small enough for them to matter: "$145.10".
export function cents(value) {
  if (value === null || value === undefined || value === "") return DASH;
  return `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// A day as the PDF's strip prints it: "Sep 22".
export function dayLabel(iso) {
  if (!iso || iso.length < 10) return DASH;
  const m = MONTH[parseInt(iso.slice(5, 7), 10) - 1];
  return m ? `${m} ${parseInt(iso.slice(8, 10), 10)}` : iso;
}

// The week's change in the open balance: "+$120 on the week", "-$5 on the week",
// "$0 on the week".
export function changeOnTheWeek(value) {
  if (value === null || value === undefined || value === "") return DASH;
  const text = money(value);
  const plus = Number(value) > 0 && text !== "$0" ? "+" : "";
  return `${plus}${text} on the week`;
}

export function count(value) {
  if (value === null || value === undefined) return DASH;
  return Number(value).toLocaleString("en-US");
}

export function trustValue(notes) {
  const raw = notes && notes.trust;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = parseFloat(String(raw).replace(/%$/, ""));
  return Number.isNaN(n) ? null : n;
}

export function isOk(row) {
  return Boolean(row && row.ok) && row.rate !== null && row.rate !== undefined;
}

// A prose cell: the notes' text; a dash when the notes leave it empty on purpose;
// "No notes this week" when the practice has no notes at all.
function prose(notes, field) {
  if (!notes) return <span className="lr-none">{NO_NOTES}</span>;
  const text = (notes[field] || "").trim();
  if (!text || text === "-" || text === DASH) return DASH;
  return text;
}

export function formatStamp(iso) {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export function RateCell({ row }) {
  if (!isOk(row)) {
    return (
      <div>
        <div>
          <span className="lr-big">{DASH}</span>
        </div>
        <div className="lr-sub">{DASH}</div>
      </div>
    );
  }
  return (
    <div>
      <div>
        <span className="lr-big">{pct1(row.rate)}</span>{" "}
        <span className="lr-sub lr-months">
          {monthsLabel(row.matured_months)}
        </span>
      </div>
      <div className="lr-sub lr-split">
        <span>Insurance {pct0(row.insurance_rate)}</span> {"·"}{" "}
        <span>Patient {pct0(row.patient_rate)}</span>
      </div>
      <div className="lr-sub">
        Open balance {money(row.open_balance)}
        {isAllowedBasis(row.ar_basis) ? (
          <>
            {" "}
            at allowed {"·"} billed {money(row.open_balance_billed)}
          </>
        ) : null}
      </div>
      {row.settlement_paced ? (
        <div className="lr-sub" data-testid="exec-settlement-paced">
          {row.settlement_paced.label}: {money(row.settlement_paced.dollars)} on{" "}
          {count(row.settlement_paced.claims)}{" "}
          {Number(row.settlement_paced.claims) === 1 ? "claim" : "claims"} (
          {money(row.settlement_paced.billed)} billed, at the trailing
          paid-to-billed ratio)
        </div>
      ) : null}
      {row.ar_basis ? (
        <div className="lr-sub lr-data-quality">
          {arBasisLine(row.ar_basis)}
        </div>
      ) : null}
      {row.expected_rate_legacy !== null &&
      row.expected_rate_legacy !== undefined ? (
        <div className="lr-sub lr-legacy">
          Net on allowed; was {pct1(row.expected_rate_legacy)} vs expected
        </div>
      ) : null}
    </div>
  );
}

// "+1.2 pts": what a waterfall bucket adds to the rate if it closes.
export function pts(value) {
  if (value === null || value === undefined) return DASH;
  const n = Number(value);
  const text = Math.abs(n).toFixed(1);
  return `${n < 0 && text !== "0.0" ? "-" : "+"}${text} pts`;
}

// The largest bucket of the gap to 98%, as the "Why" cell prints it:
// "Largest gap: Patient balances open, $1,234 on 7 claims (+12.0 pts)".
export function largestGapLine(gap) {
  if (!gap) return null;
  return `Largest gap: ${gap.label}, ${money(gap.dollars)} on ${count(gap.claims)} ${
    Number(gap.claims) === 1 ? "claim" : "claims"
  } (${pts(gap.to_98)})`;
}

// The largest category of the gap from "If all closed" to 100% on the Waterfall page
// (the same rule its bars read), printed under the gap to 98%:
// "Largest gap to 100%: No reason code from PracticeSuite, $1,234 on 7 claims (2.1 pts)".
export function largestGapTo100Line(gap) {
  if (!gap) return null;
  const points =
    gap.points === null || gap.points === undefined
      ? DASH
      : `${Number(gap.points).toFixed(1)} pts`;
  return `Largest gap to 100%: ${gap.label}, ${money(gap.dollars)} on ${count(gap.claims)} ${
    Number(gap.claims) === 1 ? "claim" : "claims"
  } (${points})`;
}

export function TrustCell({ row }) {
  if (!isOk(row)) {
    return (
      <div>
        <span className="lr-unavailable">Not available</span>
        <div className="lr-sub">
          {row && row.reason ? row.reason : "no summary row"}
        </div>
      </div>
    );
  }
  const notes = row.notes;
  if (!notes) return <span className="lr-none">{NO_NOTES}</span>;
  const trust = trustValue(notes);
  return (
    <div>
      <span className="lr-trust">
        {trust === null ? DASH : `${Math.round(trust)}%`}
      </span>
      {notes.trust_tag ? (
        <span className="lr-tag">{notes.trust_tag}</span>
      ) : null}
      {notes.trust_note ? (
        <div className="lr-sub">{notes.trust_note}</div>
      ) : null}
    </div>
  );
}

export function WhyCell({ row }) {
  const ok = isOk(row);
  const inFlight = ok ? count(row.claims_in_flight) : DASH;
  const topDenial = ok ? row.top_denial_category || DASH : DASH;
  const gap = ok ? largestGapLine(row.largest_gap) : null;
  const gap100 = ok ? largestGapTo100Line(row.largest_gap_to_100) : null;
  return (
    <div>
      <div>{prose(row && row.notes, "why")}</div>
      {gap ? <div className="lr-data lr-gap">{gap}</div> : null}
      {gap100 ? <div className="lr-data lr-gap">{gap100}</div> : null}
      <div className="lr-data">
        {inFlight} claims in flight {"·"} Top denial: {topDenial}
      </div>
    </div>
  );
}

export function WhatCell({ row }) {
  const notes = row && row.notes;
  return (
    <div>
      <div>{prose(notes, "what")}</div>
      {notes && notes.owners ? (
        <div className="lr-owner">Owner: {notes.owners}</div>
      ) : null}
    </div>
  );
}

// The five columns, in the page's order, as <td>s for one row.
export function ExecRowCells({ row }) {
  return (
    <>
      <td className="lr-practice">{row.practice || "Practice"}</td>
      <td className="lr-rate-cell">
        <RateCell row={row} />
      </td>
      <td>
        <TrustCell row={row} />
      </td>
      <td>
        <WhyCell row={row} />
      </td>
      <td>
        <WhatCell row={row} />
      </td>
    </>
  );
}
